// === GENERATED:SCREEN:ACCEPTANCE-QUESTIONS ===
// Application flow - Acceptance Questions
// Customer: SAA (NU'91)

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useWorkflow,
  useConfig,
  WorkflowPageLayout,
  getCurrentApplicationStepNumber,
  calculateApplicationSteps,
  formatPrice,
  isDebugMode,
  computeAvpDisplayPremium,
  computeWoonhuisDisplayPremium,
} from '@sluitstraat/core';
import { PremiumBreakdownCard } from '../../components/PremiumBreakdownCard';
import { USE_BREAKDOWN_UI } from '../../config/uiFlags';
import { computeInboedelTotalFromSections } from '../../utils/inboedelPricing';
import { computePolicyCostAllocation } from '../../utils/policyCost';
import { getProductEntry } from '../../products/registry';
import { acceptanceQuestionsStyles as styles } from '../../styles/commonStyles';
import { WorkflowDebugPanel } from '../../components/WorkflowDebugPanel';
import { HelpCard } from '../../components/HelpCard';
import { SubscriptionCard } from '../../components/SubscriptionCard';
import { WorkflowNavButtons } from '../../components/WorkflowNavButtons';
import { buildPremiumDebugSection, readDebugIds, readEnrichedDocument, collectProducts } from '../../utils/debugData';
import { hasAnySelectedProduct } from '../../utils/productGuards';

export const AcceptanceQuestionsScreen: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { getStepData, saveStepData, selectedProducts, data: workflowData } = useWorkflow();
  const { config } = useConfig();

  const [acceptanceData] = useState(
    getStepData('acceptance-questions') || {}
  );

  // JSON Forms state - initialize with quote data + payment data merged
  const initializeFormData = () => {
    const savedFormData = getStepData('acceptance-questions')?.formData;
    if (savedFormData && Object.keys(savedFormData).length > 0) {
      return savedFormData;
    }

    // Load payment-information-request data (has quote + payment fields)
    const paymentRequestData = getStepData('payment-information-request');
    if (paymentRequestData && Object.keys(paymentRequestData).length > 0) {
      return { ...paymentRequestData }; // This already has quote data + PP_IBAN + PP_INGDAT
    }

    // Fallback: Load quote data if AVP is selected
    if (selectedProducts?.aansprakelijkheid) {
      const quoteData = workflowData?.['aansprakelijkheid'];
      if (quoteData) {
        return { ...quoteData };
      }
    }

    return {};
  };

  const [formData, setFormData] = useState(initializeFormData());

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Validate required data on mount - redirect if missing critical data
  useEffect(() => {
    if (!hasAnySelectedProduct(selectedProducts)) {
      navigate('/workflow/product-selection', { replace: true });
      return;
    }

    // Check if client information exists
    const clientInfo = workflowData['client-information']?.clientInfo;
    if (!clientInfo || !clientInfo.email || !clientInfo.initials || !clientInfo.lastName) {
      navigate('/workflow/client-information', { replace: true });
      return;
    }

    // Check if payment information has been completed
    const paymentRequestData = getStepData('payment-information-request');
    if (!paymentRequestData || Object.keys(paymentRequestData).length === 0) {
      navigate('/workflow/payment-information', { replace: true });
      return;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only guard; re-running on selectedProducts/navigate identity would redirect mid-flow
  }, []); // Run once on mount

  // All available questions based on SAA product JSON schemas (RI fields)
  // IDs match the RI field names and local i18n translation keys (src/i18n/nl.json, en.json)
  // Dutch text for AFD backend is resolved via i18n.t() with lng:'nl'
  // All acceptance questions mapped to the products that require them (per product JSON schemas)
  // RI_ALGVRWD is handled separately as a final agreement, not shown as a regular question
  const allQuestions = [
    // Shared across AVP, Woonhuis, Inboedel, Personenauto (+ RI_EGD also for Rechtsbijstand)
    { id: 'RI_EGD', products: ['aansprakelijkheid', 'woonhuis', 'inboedel', 'personenauto', 'rechtsbijstand'] },
    // doorlopendereis reuses RI_OGB / RI_POLJUS / RI_OVRMED (same questions); the answers are
    // mirrored to its own field names (RI_OGBOOIT / RI_POLJUS8 / RI_OVRMED) at save time below.
    { id: 'RI_OGB', products: ['aansprakelijkheid', 'woonhuis', 'inboedel', 'personenauto', 'ongevallen', 'doorlopendereis'] },
    { id: 'RI_POLJUS', products: ['aansprakelijkheid', 'woonhuis', 'inboedel', 'personenauto', 'ongevallen', 'doorlopendereis'] },
    { id: 'RI_OVRMED', products: ['aansprakelijkheid', 'woonhuis', 'inboedel', 'personenauto', 'ongevallen', 'doorlopendereis'] },
    { id: 'RI_CGGDA', products: ['aansprakelijkheid', 'woonhuis', 'inboedel', 'personenauto'] },
    { id: 'RI_STVOOP', products: ['aansprakelijkheid', 'woonhuis', 'inboedel', 'personenauto'] },
    // Aansprakelijkheid-specific: aansprakelijk gesteld (nu of binnenkort verwacht).
    // US_ prefix is the AFD label requested by SAA; stored alongside the RI answers.
    { id: 'US_AANSPRP', products: ['aansprakelijkheid'] },
    // Personenauto-specific
    // RI_SCH5JR intentionally omitted — its wording is now identical to RI_EGD, and asking it
    // as a second UI question would duplicate the schade prompt. The auto-schema RI_SCH5JR
    // field is still required by the BFF, so it gets mirrored from answers['RI_EGD'] at save
    // time (see personenauto block below). If the BFF ever decommissions RI_SCH5JR, that mirror
    // can drop too.
    // Rechtsbijstand-specific (order matches rechtsbijstand.json schema)
    { id: 'RI_BGP', products: ['rechtsbijstand'] },
    { id: 'RI_BYZVRWE', products: ['rechtsbijstand'] },
    { id: 'RI_DGP', products: ['rechtsbijstand'] },
    { id: 'RI_GEWEIGE', products: ['rechtsbijstand'] },
    { id: 'RI_NGP', products: ['rechtsbijstand'] },
    { id: 'RI_STRAFRV', products: ['rechtsbijstand'] },
  ];

  // Questions that carry a supplementary `*_INFO` translation key. When the user answers 'Ja',
  // an amber info-callout is rendered below the answer toggle (the existing RI_POLJUS pattern,
  // now generalised). The matching `<id>_INFO` translation must exist in nl.json + en.json.
  // Every question with the Ja->toelichting flow carries the "Zo ja, geef dan hier een
  // toelichting." line (or richer guidance, e.g. the RI_EGD CIS text) in its *_INFO callout,
  // so the prompt between the answer toggle and the textarea is consistent across products.
  const QUESTIONS_WITH_INFO = new Set([
    'RI_POLJUS', 'RI_EGD', 'RI_OGB', 'RI_STVOOP', 'RI_OVRMED', 'RI_CGGDA', 'US_AANSPRP',
    'RI_BGP', 'RI_BYZVRWE', 'RI_DGP', 'RI_GEWEIGE', 'RI_NGP', 'RI_STRAFRV',
  ]);


  // Build the list of questions to show based on selected products
  const getQuestionsToShow = () => {
    return allQuestions.filter(q => q.products.some(p => selectedProducts?.[p]));
  };

  const questionsToShow = getQuestionsToShow();

  // Generic answers and explanations (used for all products)
  const [answers, setAnswers] = useState<Record<string, string>>(
    getStepData('acceptance-questions')?.answers || {}
  );

  const [explanations, setExplanations] = useState<Record<string, string>>(
    getStepData('acceptance-questions')?.explanations || {}
  );

  // Validation state
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  // Servicevergoeding (PP_EKST) - config-driven via prolongationFee (vaste 2.50 per product)
  const ppEkstConfig = (config as any)?.prolongationFee;
  const ppEkst = ppEkstConfig?.enabled === false ? 0 : (ppEkstConfig?.amount ?? 2.5);

  // One-time poliskosten per product (€12,50 car / €6,25 other, capped at €12,50 per package).
  // Cap-aware allocation reuses the same helper as PremiumBreakdownCard so UI + persisted data agree.
  const policyAllocation = computePolicyCostAllocation(selectedProducts, config);

  // Initialize formData structure for all selected products
  useEffect(() => {
    // Build combined formData with all selected products' data
    setFormData(() => {
      const updated: any = {};

      // Load payment-information-request (has quote + payment data for all products)
      const paymentRequestData = getStepData('payment-information-request');

      // AVP (Aansprakelijkheid) product - uses Rhion R515 with RI fields
      if (selectedProducts?.aansprakelijkheid) {
        let avpData = paymentRequestData?.avp || workflowData?.['aansprakelijkheid'] || {};
        avpData = JSON.parse(JSON.stringify(avpData)); // Deep clone

        // Ensure structure exists for Rhion RI fields
        if (!avpData.Contractdocument) avpData.Contractdocument = {};
        if (!avpData.Contractdocument.PP) avpData.Contractdocument.PP = {};
        if (!avpData.Contractdocument.PP.RI) avpData.Contractdocument.PP.RI = {};

        // Map user answers directly to RI fields (1:1 mapping with question IDs)
        avpData.Contractdocument.PP.RI.RI_EGD = answers['RI_EGD'] || 'N';
        avpData.Contractdocument.PP.RI.RI_OGB = answers['RI_OGB'] || 'N';
        avpData.Contractdocument.PP.RI.RI_POLJUS = answers['RI_POLJUS'] || 'N';
        avpData.Contractdocument.PP.RI.RI_OVRMED = answers['RI_OVRMED'] || 'N';
        avpData.Contractdocument.PP.RI.RI_CGGDA = answers['RI_CGGDA'] || 'N';
        avpData.Contractdocument.PP.RI.RI_STVOOP = answers['RI_STVOOP'] || 'N';
        avpData.Contractdocument.PP.RI.US_AANSPRP = answers['US_AANSPRP'] || 'N';
        // PP_EKST - one-time poliskosten (allocated cap-aware across the package)
        avpData.Contractdocument.PP.PP_EKST = policyAllocation.aansprakelijkheid;

        updated.avp = avpData;
      }

      // Woonhuis product - uses same RI fields as AVP
      if (selectedProducts?.woonhuis) {
        let woonhuisData = paymentRequestData?.woonhuis || workflowData?.['woonhuis'] || {};
        woonhuisData = JSON.parse(JSON.stringify(woonhuisData)); // Deep clone

        // Ensure structure exists for RI fields
        if (!woonhuisData.Contractdocument) woonhuisData.Contractdocument = {};
        if (!woonhuisData.Contractdocument.PP) woonhuisData.Contractdocument.PP = {};
        if (!woonhuisData.Contractdocument.PP.RI) woonhuisData.Contractdocument.PP.RI = {};

        // Map user answers directly to RI fields (1:1 mapping)
        woonhuisData.Contractdocument.PP.RI.RI_EGD = answers['RI_EGD'] || 'N';
        woonhuisData.Contractdocument.PP.RI.RI_OGB = answers['RI_OGB'] || 'N';
        woonhuisData.Contractdocument.PP.RI.RI_POLJUS = answers['RI_POLJUS'] || 'N';
        woonhuisData.Contractdocument.PP.RI.RI_OVRMED = answers['RI_OVRMED'] || 'N';
        woonhuisData.Contractdocument.PP.RI.RI_CGGDA = answers['RI_CGGDA'] || 'N';
        woonhuisData.Contractdocument.PP.RI.RI_STVOOP = answers['RI_STVOOP'] || 'N';
        // PP_EKST - one-time poliskosten (allocated cap-aware across the package)
        woonhuisData.Contractdocument.PP.PP_EKST = policyAllocation.woonhuis;

        updated.woonhuis = woonhuisData;
      }

      // Inboedel product - uses same RI fields as AVP
      if (selectedProducts?.inboedel) {
        let inboedelData = paymentRequestData?.inboedel || workflowData?.['inboedel'] || {};
        inboedelData = JSON.parse(JSON.stringify(inboedelData)); // Deep clone

        // Ensure structure exists for RI fields
        if (!inboedelData.Contractdocument) inboedelData.Contractdocument = {};
        if (!inboedelData.Contractdocument.PP) inboedelData.Contractdocument.PP = {};
        if (!inboedelData.Contractdocument.PP.RI) inboedelData.Contractdocument.PP.RI = {};

        // Map user answers directly to RI fields (1:1 mapping)
        inboedelData.Contractdocument.PP.RI.RI_EGD = answers['RI_EGD'] || 'N';
        inboedelData.Contractdocument.PP.RI.RI_OGB = answers['RI_OGB'] || 'N';
        inboedelData.Contractdocument.PP.RI.RI_POLJUS = answers['RI_POLJUS'] || 'N';
        inboedelData.Contractdocument.PP.RI.RI_OVRMED = answers['RI_OVRMED'] || 'N';
        inboedelData.Contractdocument.PP.RI.RI_CGGDA = answers['RI_CGGDA'] || 'N';
        inboedelData.Contractdocument.PP.RI.RI_STVOOP = answers['RI_STVOOP'] || 'N';
        // PP_EKST - one-time poliskosten (allocated cap-aware across the package)
        inboedelData.Contractdocument.PP.PP_EKST = policyAllocation.inboedel;

        updated.inboedel = inboedelData;
      }

      // Personenauto product - only RI_SCH5JR and RI_ALGVRWD per auto.json schema
      if (selectedProducts?.personenauto) {
        let personenautoData = paymentRequestData?.personenauto || workflowData?.['personenauto'] || {};
        personenautoData = JSON.parse(JSON.stringify(personenautoData)); // Deep clone

        if (!personenautoData.Contractdocument) personenautoData.Contractdocument = {};
        if (!personenautoData.Contractdocument.PP) personenautoData.Contractdocument.PP = {};
        if (!personenautoData.Contractdocument.PP.RI) personenautoData.Contractdocument.PP.RI = {};

        // RI_SCH5JR is no longer asked separately in the UI (its wording matches RI_EGD).
        // Mirror the RI_EGD answer here so the BFF still receives the field it expects in the
        // auto.json schema — same value in both, single user choice driving both data points.
        personenautoData.Contractdocument.PP.RI.RI_SCH5JR = answers['RI_EGD'] || 'N';
        personenautoData.Contractdocument.PP.RI.RI_EGD = answers['RI_EGD'] || 'N';
        personenautoData.Contractdocument.PP.RI.RI_OGB = answers['RI_OGB'] || 'N';
        personenautoData.Contractdocument.PP.RI.RI_POLJUS = answers['RI_POLJUS'] || 'N';
        personenautoData.Contractdocument.PP.RI.RI_OVRMED = answers['RI_OVRMED'] || 'N';
        personenautoData.Contractdocument.PP.RI.RI_CGGDA = answers['RI_CGGDA'] || 'N';
        personenautoData.Contractdocument.PP.RI.RI_STVOOP = answers['RI_STVOOP'] || 'N';
        personenautoData.Contractdocument.PP.RI.RI_ALGVRWD = 'J';
        // PP_EKST - one-time poliskosten (allocated cap-aware across the package)
        personenautoData.Contractdocument.PP.PP_EKST = policyAllocation.personenauto;

        updated.personenauto = personenautoData;
      }

      // Rechtsbijstand product - uses actual rechtsbijstand RI field answers
      if (selectedProducts?.rechtsbijstand) {
        let rechtsbijstandData = paymentRequestData?.rechtsbijstand || workflowData?.['rechtsbijstand'] || {};
        rechtsbijstandData = JSON.parse(JSON.stringify(rechtsbijstandData));

        if (!rechtsbijstandData.Contractdocument) rechtsbijstandData.Contractdocument = {};
        if (!rechtsbijstandData.Contractdocument.PP) rechtsbijstandData.Contractdocument.PP = {};
        if (!rechtsbijstandData.Contractdocument.PP.RI) rechtsbijstandData.Contractdocument.PP.RI = {};

        // Shared question (same ID across products)
        rechtsbijstandData.Contractdocument.PP.RI.RI_EGD = answers['RI_EGD'] || 'N';
        // Rechtsbijstand-specific questions
        rechtsbijstandData.Contractdocument.PP.RI.RI_BGP = answers['RI_BGP'] || 'N';
        rechtsbijstandData.Contractdocument.PP.RI.RI_NGP = answers['RI_NGP'] || 'N';
        rechtsbijstandData.Contractdocument.PP.RI.RI_DGP = answers['RI_DGP'] || 'N';
        rechtsbijstandData.Contractdocument.PP.RI.RI_GEWEIGE = answers['RI_GEWEIGE'] || 'N';
        rechtsbijstandData.Contractdocument.PP.RI.RI_BYZVRWE = answers['RI_BYZVRWE'] || 'N';
        rechtsbijstandData.Contractdocument.PP.RI.RI_STRAFRV = answers['RI_STRAFRV'] || 'N';
        rechtsbijstandData.Contractdocument.PP.RI.RI_SCHADV = answers['RI_SCHADV'] || 'N';
        rechtsbijstandData.Contractdocument.PP.RI.RI_ALGVRWD = 'J';
        // PP_EKST - one-time poliskosten (allocated cap-aware across the package)
        rechtsbijstandData.Contractdocument.PP.PP_EKST = policyAllocation.rechtsbijstand;

        updated.rechtsbijstand = rechtsbijstandData;
      }

      // Ongevallen (DFD / K781): DR-style RI field names — mirror the shared answers.
      if (selectedProducts?.ongevallen) {
        let ogData = paymentRequestData?.ongevallen || workflowData?.['ongevallen'] || {};
        ogData = JSON.parse(JSON.stringify(ogData)); // Deep clone

        if (!ogData.Contractdocument) ogData.Contractdocument = {};
        if (!ogData.Contractdocument.PP) ogData.Contractdocument.PP = {};
        if (!ogData.Contractdocument.PP.RI) ogData.Contractdocument.PP.RI = {};

        const ogRi = getProductEntry('ongevallen')!.riFieldNames;
        ogData.Contractdocument.PP.RI[ogRi.ogb] = answers['RI_OGB'] || 'N';
        ogData.Contractdocument.PP.RI[ogRi.ovrmed] = answers['RI_OVRMED'] || 'N';
        ogData.Contractdocument.PP.RI[ogRi.poljus] = answers['RI_POLJUS'] || 'N';

        updated.ongevallen = ogData;
      }

      // Doorlopende reis (DFD / K781). Same three questions as the other products, but the
      // schema uses different field names: RI_OGBOOIT (not RI_OGB) and RI_POLJUS8 (not
      // RI_POLJUS). Mirror the shared answers onto those DR-specific fields.
      if (selectedProducts?.doorlopendereis) {
        let drData = paymentRequestData?.doorlopendereis || workflowData?.['doorlopendereis'] || {};
        drData = JSON.parse(JSON.stringify(drData)); // Deep clone

        if (!drData.Contractdocument) drData.Contractdocument = {};
        if (!drData.Contractdocument.PP) drData.Contractdocument.PP = {};
        if (!drData.Contractdocument.PP.RI) drData.Contractdocument.PP.RI = {};

        const drRi = getProductEntry('doorlopendereis')!.riFieldNames;
        drData.Contractdocument.PP.RI[drRi.ogb] = answers['RI_OGB'] || 'N';
        drData.Contractdocument.PP.RI[drRi.ovrmed] = answers['RI_OVRMED'] || 'N';
        drData.Contractdocument.PP.RI[drRi.poljus] = answers['RI_POLJUS'] || 'N';

        updated.doorlopendereis = drData;
      }

      return updated;
    });
  }, [selectedProducts?.aansprakelijkheid, selectedProducts?.woonhuis, selectedProducts?.inboedel, selectedProducts?.personenauto, selectedProducts?.rechtsbijstand, selectedProducts?.ongevallen, selectedProducts?.doorlopendereis, workflowData, answers, explanations, ppEkst, getStepData, policyAllocation.aansprakelijkheid, policyAllocation.woonhuis, policyAllocation.inboedel, policyAllocation.personenauto, policyAllocation.rechtsbijstand]);


  // Persist the answer draft on every change (answers + explanations only; formData stays the
  // last validated build from Doorgaan): stepper/logo navigation must never drop an answer, and
  // the stepper's forward gate reads the workflow, not this screen's state.
  const persistDraft = (nextAnswers: Record<string, string>, nextExplanations: Record<string, string>) => {
    saveStepData('acceptance-questions', {
      ...(getStepData('acceptance-questions') || {}),
      answers: nextAnswers,
      explanations: nextExplanations,
    });
  };

  const handleQuestionChange = (questionId: string, value: string) => {
    const updatedAnswers = { ...answers, [questionId]: value };
    setAnswers(updatedAnswers);

    // Clear validation error when question is answered
    if (validationErrors[questionId]) {
      setValidationErrors(prev => {
        const { [questionId]: _, ...rest } = prev;
        return rest;
      });
    }

    // Clear explanation if answer changes to 'N'
    let nextExplanations = explanations;
    if (value === 'N' && explanations[questionId]) {
      const updatedExplanations = { ...explanations };
      delete updatedExplanations[questionId];
      nextExplanations = updatedExplanations;
      setExplanations(updatedExplanations);
      // Also clear explanation validation error
      if (validationErrors[`explanation_${questionId}`]) {
        setValidationErrors(prev => {
          const { [`explanation_${questionId}`]: _, ...rest } = prev;
          return rest;
        });
      }
    }
    persistDraft(updatedAnswers, nextExplanations);
  };

  const handleExplanationChange = (questionId: string, explanation: string) => {
    const updatedExplanations = { ...explanations, [questionId]: explanation };
    setExplanations(updatedExplanations);
    persistDraft(answers, updatedExplanations);

    // Clear validation error when explanation is provided
    if (explanation && validationErrors[`explanation_${questionId}`]) {
      setValidationErrors(prev => {
        const { [`explanation_${questionId}`]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  // Validate all questions - stores error types, not translated strings
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    questionsToShow.forEach(question => {
      // Check if question is answered
      if (!answers[question.id]) {
        errors[question.id] = 'answerRequired';
      }
      // Check if explanation is provided when answer is 'J' (except RI_POLJUS which only shows info)
      else if (answers[question.id] === 'J' && question.id !== 'RI_POLJUS' && !explanations[question.id]?.trim()) {
        errors[`explanation_${question.id}`] = 'explanationRequired';
      }
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Helper to get translated error message
  const getErrorMessage = (errorType: string): string => {
    return t(`screens.acceptanceQuestions.validation.${errorType}`);
  };

  const handleContinue = () => {
    setHasAttemptedSubmit(true);

    // Validate before proceeding
    if (!validateForm()) {
      // Scroll to first error - use setTimeout to allow state update before reading errors
      setTimeout(() => {
        const errorElements = document.querySelectorAll('[data-error="true"], .field-error');
        if (errorElements.length > 0) {
          errorElements[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 50);
      return;
    }

    // formData already has all products' data merged with QA questions
    saveStepData('acceptance-questions', {
      ...acceptanceData,
      formData: formData, // Contains data for all selected products with QA arrays
      answers,
      explanations
    });
    navigate('/workflow/confirmation');
  };

  const handleBack = () => {
    navigate('/workflow/payment-information');
  };

  const currentStep = getCurrentApplicationStepNumber('/workflow/acceptance-questions');
  const totalSteps = calculateApplicationSteps();

  const selectedProductCount = [
    selectedProducts?.aansprakelijkheid,
    selectedProducts?.inboedel,
    selectedProducts?.woonhuis,
    selectedProducts?.personenauto,
  ].filter(Boolean).length;

  const subscriptionFeeConfig = (config as any)?.subscriptionFee;
  const subscriptionFee = subscriptionFeeConfig?.enabled
    ? (selectedProductCount > 2 ? (subscriptionFeeConfig.tier3 || 20.00) : (subscriptionFeeConfig.tier1 || 12.50))
    : 0;
  const prolongationFeeConfig = (config as any)?.prolongationFee;
  const prolongationFee = prolongationFeeConfig?.enabled ? (prolongationFeeConfig.amount || 2.50) : 0;

  // Get individual product premium (as a number). AVP / Inboedel / Woonhuis always read workflow
  // data so the per-product line shows only the product discount (not the BFF's package discount
  // layered on top). Personenauto / Rechtsbijstand fall back to packageProduct.PP_TTOT when the
  // enriched doc is available (they have no per-product UI discount, so package is fine to show here).
  const getProductPremiumNumber = (productType: 'aansprakelijkheid' | 'inboedel' | 'woonhuis' | 'personenauto' | 'rechtsbijstand'): number => {
    if (productType === 'aansprakelijkheid') {
      const avp = computeAvpDisplayPremium(workflowData?.['aansprakelijkheid'], config as any);
      return avp > 0 ? avp : 0;
    }
    if (productType === 'inboedel') {
      // Use the shared per-section helper so this matches the Inboedel screen's badge sum exactly.
      const inb = computeInboedelTotalFromSections(workflowData?.['inboedel'], config as any);
      return inb > 0 ? inb : 0;
    }
    if (productType === 'woonhuis') {
      const wh = computeWoonhuisDisplayPremium(workflowData?.['woonhuis'], config as any);
      return wh > 0 ? wh : 0;
    }
    try {
      const enrichedDoc = sessionStorage.getItem('enriched_calculation_document');
      if (enrichedDoc) {
        const doc = JSON.parse(enrichedDoc);
        const packageProducts = doc?.Pakket?.[0]?.Onderdeel || doc?.Contract || [];
        const pkg = productType === 'personenauto'
          ? packageProducts.find((p: any) => p.PP?.OB)
          : packageProducts.find((p: any) => p.PP?.WC && p.PP?.PP_EXTERN === 'RB');
        if (pkg?.PP?.PP_TTOT) return Number(pkg.PP.PP_TTOT) || 0;
      }
    } catch (error) { /* fall back */ }
    const premium = workflowData?.[productType]?.Contractdocument?.PP?.PP_TTOT;
    return premium ? parseFloat(premium) : 0;
  };

  const getProductPremium = (productType: 'aansprakelijkheid' | 'inboedel' | 'woonhuis' | 'personenauto' | 'rechtsbijstand'): string => {
    const n = getProductPremiumNumber(productType);
    return n > 0 ? formatPrice(n) : '0,00';
  };

  // Calculate total monthly premium by summing per-product ex-provision helper values (same as PaymentInformationScreen).
  // PK_TTOT is not used here because it includes the provision, which would mismatch the per-product ex-provision display.
  const calculateTotalMonthly = () => {
    const productTypes: Array<'woonhuis' | 'inboedel' | 'aansprakelijkheid' | 'personenauto' | 'rechtsbijstand'> = [
      'woonhuis', 'inboedel', 'aansprakelijkheid', 'personenauto', 'rechtsbijstand',
    ];
    let total = 0;
    for (const type of productTypes) {
      if (selectedProducts?.[type]) total += getProductPremiumNumber(type);
    }
    return formatPrice(total + subscriptionFee + prolongationFee);
  };

  // Collected current-page data for the shared debug panel, incl. the acceptance answers.
  const debugCollected: Record<string, unknown> = {
    ids: readDebugIds(),
    relation: workflowData?.['client-information']?.clientInfo ?? null,
    payment: getStepData('payment-information-request') ?? null,
    premium: buildPremiumDebugSection(selectedProducts, workflowData, config),
    acceptanceQuestions: { answers, explanations },
    formData,
    package: readEnrichedDocument(),
    products: collectProducts(selectedProducts, workflowData),
  };

  return (
    <WorkflowPageLayout currentStep={currentStep} totalSteps={totalSteps} flowType="application">
      <main style={styles.page}>
        <div style={styles.backgroundDecoration}></div>
        <div style={styles.container}>
          <header style={styles.header}>
            <h1 style={styles.title}>{t('screens.paymentInfo.title')}</h1>
            <p style={styles.subtitle}>{t('screens.paymentInfo.subtitle')}</p>
          </header>

          <div style={styles.contentWrapper} className="acceptance-content-wrapper">
            {/* Main Content */}
            <div style={styles.mainContent} className="acceptance-main-content">
              <div style={styles.card}>
            {/* Section Header */}
            <div style={styles.sectionHeader}>
              <svg style={styles.sectionIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 11l3 3L22 4"></path>
                <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
              </svg>
              <h2 style={styles.sectionTitle}>{t('screens.acceptanceQuestions.title')}</h2>
            </div>
            <p style={styles.sectionDescription}>{t('screens.acceptanceQuestions.subtitle')}</p>

            {/* All acceptance questions for selected products */}
            {questionsToShow.length > 0 && (
              <>
                {questionsToShow.map((question) => (
                  <div key={question.id} id={`question-${question.id}`} style={styles.questionSection}>
                    <h3 style={styles.questionTitle}>
                      {t(`screens.acceptanceQuestions.questions.${question.id}`)}<span style={styles.requiredNoWrap}>&nbsp;*</span>
                    </h3>
                    <div style={{
                      ...styles.buttonGroup,
                      ...(hasAttemptedSubmit && validationErrors[question.id] ? styles.buttonGroupError : {})
                    }}>
                      <button
                        type="button"
                        onClick={() => handleQuestionChange(question.id, 'J')}
                        style={{
                          ...styles.optionButton,
                          ...(answers[question.id] === 'J' ? styles.optionButtonSelected : {}),
                          ...(hasAttemptedSubmit && validationErrors[question.id] && !answers[question.id] ? styles.optionButtonError : {}),
                        }}
                      >
                        {t('screens.acceptanceQuestions.answers.yes')}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleQuestionChange(question.id, 'N')}
                        style={{
                          ...styles.optionButton,
                          ...(answers[question.id] === 'N' ? styles.optionButtonSelected : {}),
                          ...(hasAttemptedSubmit && validationErrors[question.id] && !answers[question.id] ? styles.optionButtonError : {}),
                        }}
                      >
                        {t('screens.acceptanceQuestions.answers.no')}
                      </button>
                    </div>
                    {hasAttemptedSubmit && validationErrors[question.id] && (
                      <p style={styles.errorMessage}>{getErrorMessage(validationErrors[question.id])}</p>
                    )}

                    {/* Info notice shown when answered 'Ja' for questions that carry an *_INFO
                        translation key. RI_POLJUS_INFO explains which "yes" cases qualify; the
                        others (RI_EGD / RI_OGB) carry CIS/scope guidance per the SAA acceptance-
                        questions spec. `whiteSpace: pre-line` preserves the line breaks + bullet
                        structure inside the translation string. */}
                    {QUESTIONS_WITH_INFO.has(question.id) && answers[question.id] === 'J' && (
                      <div style={styles.answerInfoCallout}>
                        <svg style={styles.answerInfoIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10"></circle>
                          <line x1="12" y1="8" x2="12" y2="12"></line>
                          <line x1="12" y1="16" x2="12.01" y2="16"></line>
                        </svg>
                        <p style={styles.answerInfoText}>
                          {t(`screens.acceptanceQuestions.questions.${question.id}_INFO`)}
                        </p>
                      </div>
                    )}

                    {/* Show explanation field when answer is 'J' (except RI_POLJUS which only shows info) */}
                    {question.id !== 'RI_POLJUS' && answers[question.id] === 'J' && (
                      <div style={styles.explanationContainer}>
                        <label style={styles.explanationLabel}>
                          {t('screens.acceptanceQuestions.fields.explanation')} <span style={styles.required}>*</span>
                        </label>
                        <textarea
                          value={explanations[question.id] || ''}
                          onChange={(e) => handleExplanationChange(question.id, e.target.value)}
                          style={{
                            ...styles.explanationTextarea,
                            ...(hasAttemptedSubmit && validationErrors[`explanation_${question.id}`] ? styles.textareaError : {})
                          }}
                          placeholder={t('screens.acceptanceQuestions.placeholders.explanation')}
                          rows={4}
                        />
                        {hasAttemptedSubmit && validationErrors[`explanation_${question.id}`] && (
                          <p style={styles.errorMessage}>{getErrorMessage(validationErrors[`explanation_${question.id}`])}</p>
                        )}
                      </div>
                    )}

                  </div>
                ))}
              </>
            )}

            {/* Important Notice */}
            <div style={styles.importantNotice}>
              <div style={styles.noticeHeader}>
                <svg style={styles.noticeIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
                <strong style={styles.noticeTitle}>{t('screens.acceptanceQuestions.important.title')}</strong>
              </div>
              <p style={styles.noticeText}>{t('screens.acceptanceQuestions.important.description')}</p>
            </div>
              </div>


            <WorkflowNavButtons className="acceptance-footer" onBack={handleBack} onNext={handleContinue} />
            </div>

            {/* Sidebar */}
            <aside style={styles.sidebar} className="acceptance-sidebar" tabIndex={-1}>
              {/* Summary Card — toggle: USE_BREAKDOWN_UI=true shows shared breakdown card, false shows classic product list. */}
              <div style={styles.summaryCard} className="acceptance-summary-card" tabIndex={-1}>
                <div style={styles.sidebarHeader}>
                  <svg style={styles.sidebarIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" tabIndex={-1} aria-hidden="true">
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                    <polyline points="9 22 9 12 15 12 15 22"></polyline>
                  </svg>
                  <h3 style={styles.sidebarTitle}>{t('screens.paymentInfo.sidebar.summary')}</h3>
                </div>
                {USE_BREAKDOWN_UI ? (
                  <PremiumBreakdownCard skipChainGate />
                ) : (
                  <>
                    {/* Product list order: Personenauto → Woonhuis → Inboedel → AVP → Rechtsbijstand */}
                    <div style={styles.productList}>
                      {selectedProducts?.personenauto && (
                        <div style={styles.productItem}>
                          <div style={styles.productItemLabel}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--primary-color, #056666)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 17h14M5 17a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h1l2-3h8l2 3h1a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2"/><circle cx="7.5" cy="17" r="2"/><circle cx="16.5" cy="17" r="2"/></svg>
                            <span style={styles.productName}>{t('products.personenauto.name')}</span>
                          </div>
                          <span style={styles.productPrice}>€{getProductPremium('personenauto')}</span>
                        </div>
                      )}
                      {selectedProducts?.woonhuis && (
                        <div style={styles.productItem}>
                          <div style={styles.productItemLabel}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--primary-color, #056666)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                            <span style={styles.productName}>{t('products.woonhuis.name')}</span>
                          </div>
                          <span style={styles.productPrice}>€{getProductPremium('woonhuis')}</span>
                        </div>
                      )}
                      {selectedProducts?.inboedel && (
                        <div style={styles.productItem}>
                          <div style={styles.productItemLabel}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--primary-color, #056666)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 9v11a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V9"/><path d="M9 22V12h6v10"/><path d="M2 10.6L12 2l10 8.6"/></svg>
                            <span style={styles.productName}>{t('products.inboedel.name')}</span>
                          </div>
                          <span style={styles.productPrice}>€{getProductPremium('inboedel')}</span>
                        </div>
                      )}
                      {selectedProducts?.aansprakelijkheid && (
                        <div style={styles.productItem}>
                          <div style={styles.productItemLabel}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--primary-color, #056666)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                            <span style={styles.productName}>{t('products.aansprakelijkheid.name')}</span>
                          </div>
                          <span style={styles.productPrice}>€{getProductPremium('aansprakelijkheid')}</span>
                        </div>
                      )}
                      {selectedProducts?.rechtsbijstand && (
                        <div style={styles.productItem}>
                          <div style={styles.productItemLabel}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--primary-color, #056666)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v18M2 7l10-4 10 4M2 7l4 9h4M14 16h4l4-9"/><circle cx="6" cy="16" r="2"/><circle cx="18" cy="16" r="2"/></svg>
                            <span style={styles.productName}>{t('products.rechtsbijstand.name')}</span>
                          </div>
                          <span style={styles.productPrice}>€{getProductPremium('rechtsbijstand')}</span>
                        </div>
                      )}
                    </div>
                    <div style={styles.productListDivider}></div>
                    <div style={styles.feeList}>
                      <div style={styles.feeRow}>
                        <span style={styles.feeLabel}>{t('subscription.label')}</span>
                        <span style={styles.feeLabel}>€{formatPrice(subscriptionFee)}</span>
                      </div>
                      <div style={styles.feeRow}>
                        <span style={styles.feeLabel}>{t('prolongation.label')}</span>
                        <span style={styles.feeLabel}>€{formatPrice(prolongationFee)}</span>
                      </div>
                    </div>
                    <div style={styles.divider}></div>
                    <div style={styles.totalRow}>
                      <span style={styles.totalLabel}>{t('screens.paymentInfo.sidebar.totalMonthly')}</span>
                      <span style={styles.totalAmount}>€{calculateTotalMonthly()}</span>
                    </div>
                    <div style={styles.taxNote}>{t('screens.offerSummary.includingInsuranceTax')}</div>
                  </>
                )}
              </div>

              {/* Help Card */}
              <HelpCard className="acceptance-help-card" />
              <SubscriptionCard className="acceptance-benefits-card" />
            </aside>
          </div>

          {isDebugMode() && <WorkflowDebugPanel collected={debugCollected} />}

        </div>
      </main>
    </WorkflowPageLayout>
  );
};

export default AcceptanceQuestionsScreen;
